// ============================================================================
// STRATEGY MONITOR / DEBUG CONSOLE — Real-time log viewer (v1.0)
// ============================================================================
// Temporary dev tool. Disable via REACT_APP_ENABLE_MONITOR_PAGE=false.
// ============================================================================

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Chip, Button, IconButton, TextField,
  Select, MenuItem, FormControl, InputLabel, Tooltip, Switch,
  FormControlLabel, Collapse, Alert, Badge, Divider,
} from '@mui/material';
import {
  Terminal as TerminalIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Delete as ClearIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  FiberManualRecord as DotIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  BugReport as DebugIcon,
} from '@mui/icons-material';
import {
  getEvents, subscribe, clearEvents, connectSSE, disconnectSSE,
  getConnectionStatus, subscribeStatus, fetchRecentEvents, exportIssues,
} from '../services/MonitorEventStore';

// ── Level colors & icons ────────────────────────────────────────────────
const LEVEL_CONFIG = {
  error: { color: '#EF4444', bg: 'rgba(239,68,68,0.08)', icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
  warn:  { color: '#FBBF24', bg: 'rgba(245,158,11,0.08)', icon: <WarningIcon sx={{ fontSize: 14 }} /> },
  info:  { color: '#3b82f6', bg: 'rgba(96,165,250,0.06)', icon: <InfoIcon sx={{ fontSize: 14 }} /> },
  debug: { color: '#6B7280', bg: 'transparent', icon: <DebugIcon sx={{ fontSize: 14 }} /> },
};

const CATEGORY_COLORS = {
  scan: '#A855F7', gate: '#EF4444', entry: '#00FF94', position: '#3b82f6',
  exit: '#FBBF24', rescue: '#EF4444', risk: '#EF4444', network: '#3b82f6',
  data: '#00FF94', ui: '#F97316', console: '#9CA3AF',
};

// ── Event Row ───────────────────────────────────────────────────────────
function EventRow({ event, isExpanded, onToggle }) {
  const level = LEVEL_CONFIG[event.level] || LEVEL_CONFIG.debug;
  const catColor = CATEGORY_COLORS[event.category] || '#6B7280';
  const time = new Date(event.ts);
  const timeStr = time.toLocaleTimeString('en-GB', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const msStr = `.${String(time.getMilliseconds()).padStart(3, '0')}`;

  return (
    <Box
      onClick={onToggle}
      sx={{
        px: 1.5, py: 0.6, cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.03)',
        bgcolor: level.bg, '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
        fontFamily: '"JetBrains Mono", "Fira Code", monospace', fontSize: '0.78rem',
        display: 'flex', alignItems: 'flex-start', gap: 1,
      }}
    >
      {/* Time */}
      <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', fontFamily: 'inherit', minWidth: 90, flexShrink: 0, pt: 0.1 }}>
        {timeStr}<span style={{ color: '#4B5563' }}>{msStr}</span>
      </Typography>

      {/* Level icon */}
      <Box sx={{ color: level.color, pt: 0.2, flexShrink: 0 }}>{level.icon}</Box>

      {/* Category */}
      <Chip
        label={event.category}
        size="small"
        sx={{
          height: 18, fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase',
          bgcolor: `${catColor}20`, color: catColor, border: `1px solid ${catColor}40`,
          flexShrink: 0,
        }}
      />

      {/* Symbol */}
      {event.symbol && (
        <Chip
          label={event.symbol}
          size="small"
          sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.06)', color: '#D1D5DB', flexShrink: 0 }}
        />
      )}

      {/* Action */}
      <Typography sx={{
        color: level.color, fontWeight: 700, fontSize: '0.72rem', fontFamily: 'inherit',
        flexShrink: 0, minWidth: 0,
      }}>
        {event.action}
      </Typography>

      {/* Message */}
      <Typography sx={{
        color: '#D1D5DB', fontSize: '0.72rem', fontFamily: 'inherit',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isExpanded ? 'normal' : 'nowrap',
        flex: 1, minWidth: 0,
      }}>
        {event.message}
      </Typography>
    </Box>
  );
}

// ── Expanded Detail ─────────────────────────────────────────────────────
function EventDetail({ event }) {
  if (!event.data && !event.strategyId && !event.runId && !event.positionId) return null;

  return (
    <Box sx={{
      px: 2, py: 1, bgcolor: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.05)',
      fontFamily: '"JetBrains Mono", "Fira Code", monospace', fontSize: '0.7rem',
    }}>
      {event.strategyId && <Typography sx={{ color: '#6B7280', fontSize: '0.68rem' }}>Strategy: {event.strategyId}</Typography>}
      {event.runId && <Typography sx={{ color: '#6B7280', fontSize: '0.68rem' }}>Run: {event.runId}</Typography>}
      {event.positionId && <Typography sx={{ color: '#6B7280', fontSize: '0.68rem' }}>Position: {event.positionId}</Typography>}
      {event.fingerprint && <Typography sx={{ color: '#4B5563', fontSize: '0.65rem' }}>FP: {event.fingerprint}</Typography>}
      {event.data && (
        <Box sx={{ mt: 0.5 }}>
          {event.data.stack ? (
            <Box sx={{ color: '#EF4444', whiteSpace: 'pre-wrap', fontSize: '0.65rem', maxHeight: 200, overflow: 'auto' }}>
              {event.data.stack}
            </Box>
          ) : (
            <Box sx={{ color: '#9CA3AF', whiteSpace: 'pre-wrap', fontSize: '0.65rem', maxHeight: 200, overflow: 'auto' }}>
              {JSON.stringify(event.data, null, 2)}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// ── Error Summary Box ───────────────────────────────────────────────────
function ErrorSummary({ events, onFilterFingerprint }) {
  const errorGroups = useMemo(() => {
    const groups = {};
    events.filter(e => e.level === 'error' || e.level === 'warn').forEach(e => {
      const fp = e.fingerprint || e.action;
      if (!groups[fp]) groups[fp] = { action: e.action, message: e.message, level: e.level, count: 0, latest: 0 };
      groups[fp].count++;
      groups[fp].latest = Math.max(groups[fp].latest, e.ts);
    });
    return Object.entries(groups)
      .sort(([, a], [, b]) => b.latest - a.latest)
      .slice(0, 10);
  }, [events]);

  if (errorGroups.length === 0) return null;

  const totalErrors = errorGroups.reduce((sum, [, g]) => sum + (g.level === 'error' ? g.count : 0), 0);
  const totalWarns = errorGroups.reduce((sum, [, g]) => sum + (g.level === 'warn' ? g.count : 0), 0);

  return (
    <Paper sx={{ mb: 1.5, p: 1.5, border: '1px solid rgba(239,68,68,0.2)', bgcolor: 'rgba(239,68,68,0.04)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ErrorIcon sx={{ color: '#EF4444', fontSize: 18 }} />
        <Typography sx={{ color: '#EF4444', fontWeight: 700, fontSize: '0.85rem' }}>
          Issues ({totalErrors} errors, {totalWarns} warnings)
        </Typography>
      </Box>
      {errorGroups.map(([fp, group]) => (
        <Box
          key={fp}
          onClick={() => onFilterFingerprint(fp)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.4,
            cursor: 'pointer', borderRadius: 1,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
          }}
        >
          <Chip
            label={group.count}
            size="small"
            sx={{
              height: 20, minWidth: 28, fontSize: '0.7rem', fontWeight: 700,
              bgcolor: group.level === 'error' ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
              color: group.level === 'error' ? '#EF4444' : '#FBBF24',
            }}
          />
          <Typography sx={{ color: '#D1D5DB', fontSize: '0.75rem', fontWeight: 600 }}>{group.action}</Typography>
          <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {group.message?.substring(0, 80)}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
}

// ── Main Monitor Page ───────────────────────────────────────────────────
export default function Monitor() {
  const [allEvents, setAllEvents] = useState(() => getEvents());
  const [connectionStatus, setConnectionStatus] = useState(getConnectionStatus());
  const [paused, setPaused] = useState(false);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [fingerprintFilter, setFingerprintFilter] = useState(null);
  const [strategyFilter, setStrategyFilter] = useState('all');
  const [strategies, setStrategies] = useState([]);
  const listRef = useRef(null);
  const autoScrollRef = useRef(true);

  // Load strategies
  useEffect(() => {
    const STRATEGY_ENGINE_URL = process.env.REACT_APP_STRATEGY_ENGINE_URL || 'https://strategy-engine.houman-ghavamzadeh.workers.dev';
    fetch(`${STRATEGY_ENGINE_URL}/api/strategies`)
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.data || []);
        setStrategies(list);
      }).catch(() => {});
  }, []);

  // Connect SSE on mount
  useEffect(() => {
    connectSSE(strategyFilter !== 'all' ? strategyFilter : null);
    fetchRecentEvents(strategyFilter !== 'all' ? strategyFilter : null);
    return () => disconnectSSE();
  }, [strategyFilter]);

  // Subscribe to events
  useEffect(() => {
    const unsub = subscribe(() => {
      if (!paused) setAllEvents([...getEvents()]);
    });
    const unsubStatus = subscribeStatus(setConnectionStatus);
    return () => { unsub(); unsubStatus(); };
  }, [paused]);

  // Auto-scroll
  useEffect(() => {
    if (autoScrollRef.current && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [allEvents]);

  const handleScroll = useCallback(() => {
    if (!listRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    autoScrollRef.current = scrollHeight - scrollTop - clientHeight < 50;
  }, []);

  // Filtered events
  const filteredEvents = useMemo(() => {
    let result = allEvents;
    if (errorsOnly) result = result.filter(e => e.level === 'error' || e.level === 'warn');
    if (levelFilter !== 'all') result = result.filter(e => e.level === levelFilter);
    if (categoryFilter !== 'all') result = result.filter(e => e.category === categoryFilter);
    if (sourceFilter !== 'all') result = result.filter(e => e.source === sourceFilter);
    if (fingerprintFilter) result = result.filter(e => e.fingerprint === fingerprintFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(e =>
        e.message?.toLowerCase().includes(q) ||
        e.action?.toLowerCase().includes(q) ||
        e.symbol?.toLowerCase().includes(q) ||
        e.category?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [allEvents, errorsOnly, levelFilter, categoryFilter, sourceFilter, search, fingerprintFilter]);

  const handleCopyIssues = useCallback(() => {
    const report = exportIssues(filteredEvents, { strategyId: strategyFilter !== 'all' ? strategyFilter : undefined });
    navigator.clipboard.writeText(report).then(() => {
      // Success feedback could be a toast — for now just log
    }).catch(() => {});
  }, [filteredEvents, strategyFilter]);

  const handleClearFilters = () => {
    setSearch('');
    setLevelFilter('all');
    setCategoryFilter('all');
    setSourceFilter('all');
    setErrorsOnly(false);
    setFingerprintFilter(null);
  };

  const statusColor = connectionStatus === 'connected' ? '#00FF94' : connectionStatus === 'connecting' ? '#FBBF24' : '#EF4444';

  const errorCount = allEvents.filter(e => e.level === 'error').length;
  const warnCount = allEvents.filter(e => e.level === 'warn').length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)', minHeight: 400 }}>
      {/* Header */}
      <Box sx={{
        p: 2, borderRadius: 2, mb: 1.5,
        background: 'linear-gradient(135deg, rgba(11,17,32,0.85) 0%, rgba(24,27,33,0.9) 100%)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TerminalIcon sx={{ fontSize: 28, color: '#00FF94' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: 'white' }}>
            Strategy Monitor
          </Typography>
          <Tooltip title={connectionStatus}>
            <DotIcon sx={{ fontSize: 14, color: statusColor, ml: 0.5 }} />
          </Tooltip>
          <Typography sx={{ color: statusColor, fontSize: '0.72rem', fontWeight: 600 }}>
            {connectionStatus}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
          {errorCount > 0 && (
            <Chip
              icon={<ErrorIcon sx={{ fontSize: 14 }} />}
              label={`${errorCount} errors`}
              size="small"
              sx={{ bgcolor: 'rgba(239,68,68,0.15)', color: '#EF4444', fontWeight: 700, fontSize: '0.72rem', height: 26 }}
              onClick={() => { setErrorsOnly(true); setLevelFilter('error'); }}
            />
          )}
          {warnCount > 0 && (
            <Chip
              icon={<WarningIcon sx={{ fontSize: 14 }} />}
              label={`${warnCount} warns`}
              size="small"
              sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#FBBF24', fontWeight: 700, fontSize: '0.72rem', height: 26 }}
              onClick={() => { setErrorsOnly(true); setLevelFilter('warn'); }}
            />
          )}

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={strategyFilter}
              onChange={(e) => setStrategyFilter(e.target.value)}
              sx={{ fontSize: '0.78rem', height: 32 }}
            >
              <MenuItem value="all">All Strategies</MenuItem>
              {strategies.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title={paused ? 'Resume stream' : 'Pause stream'}>
            <IconButton size="small" onClick={() => setPaused(!paused)} sx={{ color: paused ? '#FBBF24' : '#9CA3AF' }}>
              {paused ? <PlayIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Toggle filters">
            <IconButton size="small" onClick={() => setShowFilters(!showFilters)} sx={{ color: showFilters ? '#00FF94' : '#9CA3AF' }}>
              <FilterIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Clear view">
            <IconButton size="small" onClick={() => { clearEvents(); setAllEvents([]); }} sx={{ color: '#9CA3AF' }}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Copy all issues to clipboard">
            <IconButton size="small" onClick={handleCopyIssues} sx={{ color: '#9CA3AF', '&:hover': { color: '#00FF94' } }}>
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Reconnect">
            <IconButton size="small" onClick={() => connectSSE(strategyFilter !== 'all' ? strategyFilter : null)} sx={{ color: '#9CA3AF' }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Collapse in={showFilters}>
        <Paper sx={{ p: 1.5, mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search messages..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 16, mr: 0.5, color: '#6B7280' }} /> }}
            sx={{ width: 200, '& input': { fontSize: '0.78rem' } }}
          />
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Level</InputLabel>
            <Select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)} label="Level" sx={{ fontSize: '0.78rem', height: 32 }}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="warn">Warn</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="debug">Debug</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Category</InputLabel>
            <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} label="Category" sx={{ fontSize: '0.78rem', height: 32 }}>
              <MenuItem value="all">All</MenuItem>
              {['scan','gate','entry','position','exit','rescue','risk','network','data','ui','console'].map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Source</InputLabel>
            <Select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} label="Source" sx={{ fontSize: '0.78rem', height: 32 }}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="backend">Backend</MenuItem>
              <MenuItem value="frontend">Frontend</MenuItem>
              <MenuItem value="broker">Broker</MenuItem>
              <MenuItem value="system">System</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch size="small" checked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)} />}
            label={<Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>Errors only</Typography>}
          />
          {(search || levelFilter !== 'all' || categoryFilter !== 'all' || sourceFilter !== 'all' || errorsOnly || fingerprintFilter) && (
            <Button size="small" onClick={handleClearFilters} sx={{ fontSize: '0.72rem', color: '#9CA3AF' }}>
              Clear filters
            </Button>
          )}
          {fingerprintFilter && (
            <Chip
              label={`FP: ${fingerprintFilter}`}
              size="small"
              onDelete={() => setFingerprintFilter(null)}
              sx={{ bgcolor: 'rgba(239,68,68,0.1)', color: '#EF4444', fontSize: '0.68rem' }}
            />
          )}
          <Typography sx={{ ml: 'auto', color: '#6B7280', fontSize: '0.72rem' }}>
            {filteredEvents.length} / {allEvents.length} events
          </Typography>
        </Paper>
      </Collapse>

      {/* Error Summary */}
      <ErrorSummary events={allEvents} onFilterFingerprint={setFingerprintFilter} />

      {/* Event List */}
      <Paper
        ref={listRef}
        onScroll={handleScroll}
        sx={{
          flex: 1, overflow: 'auto', borderRadius: 2,
          bgcolor: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 3 },
        }}
      >
        {filteredEvents.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <TerminalIcon sx={{ fontSize: 48, color: '#374151', mb: 1 }} />
            <Typography sx={{ color: '#6B7280', fontSize: '0.85rem' }}>
              {allEvents.length === 0 ? 'Waiting for events...' : 'No events match filters'}
            </Typography>
            {paused && (
              <Typography sx={{ color: '#FBBF24', fontSize: '0.78rem', mt: 0.5 }}>
                Stream is paused
              </Typography>
            )}
          </Box>
        ) : (
          filteredEvents.slice(-1000).map(event => (
            <React.Fragment key={event.id}>
              <EventRow
                event={event}
                isExpanded={expandedId === event.id}
                onToggle={() => setExpandedId(expandedId === event.id ? null : event.id)}
              />
              {expandedId === event.id && <EventDetail event={event} />}
            </React.Fragment>
          ))
        )}
      </Paper>

      {/* Footer status */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, px: 1 }}>
        <Typography sx={{ color: '#4B5563', fontSize: '0.68rem' }}>
          Buffer: {allEvents.length}/5000 | Showing: {Math.min(filteredEvents.length, 1000)}
        </Typography>
        {paused && (
          <Chip label="PAUSED" size="small" sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#FBBF24', height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
        )}
      </Box>
    </Box>
  );
}
